/**
 * kanban/KanbanBoard.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Top-level orchestrator for the Kanban view.
 *
 * Responsibilities:
 *  - Accept the flat task list + handlers from ProjectManagementDashboard
 *  - Instantiate useKanbanDrag and distribute state + handlers to columns
 *  - Render horizontal-scrollable column layout
 *  - Forward "add task" clicks (pre-filling the status) to onAddTask
 *  - Show loading skeleton while tasks are being fetched
 *  - Show empty-board state when there are truly zero tasks
 *
 * This component owns NO data-fetching, NO modal state, and NO business logic —
 * all of that stays in the parent (index.jsx).
 */

import React, { useMemo } from 'react';
import { KANBAN_COLUMNS, COLUMN_MIN_WIDTH } from './constants';
import useKanbanDrag from './useKanbanDrag';
import KanbanColumn from './KanbanColumn';

// Column keys in the order they appear on the board
const COLUMN_KEYS = KANBAN_COLUMNS.map((c) => c.key);

// ── Loading skeleton ──────────────────────────────────────────────────────────

function ColumnSkeleton() {
    return (
        <div
            className="flex-shrink-0 flex flex-col gap-3 animate-pulse"
            style={{ minWidth: COLUMN_MIN_WIDTH, width: COLUMN_MIN_WIDTH }}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-1 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-slate-200 dark:bg-slate-700" />
                <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-4 w-4 rounded-full bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="h-0.5 rounded-full bg-slate-200 dark:bg-slate-700 mb-1" />
            {/* Fake cards */}
            {[80, 60, 90].map((h, i) => (
                <div
                    key={i}
                    className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4"
                    style={{ height: h + 40 }}
                >
                    <div className="h-3 w-12 bg-slate-200 dark:bg-slate-700 rounded-full mb-3" />
                    <div className="h-4 w-full bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                    <div className="h-3 w-3/4 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
            ))}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {object[]}  tasks           - flat array of task objects from the API
 * @param {boolean}   loading         - true while parent is fetching tasks
 * @param {Function}  onStatusChange  - (taskId, newStatus) => void
 * @param {Function}  onCardClick     - (task) => void  — opens the detail drawer
 * @param {Function}  onAddTask       - (prefillStatus) => void — opens create-task modal
 */
export default function KanbanBoard({
    tasks = [],
    loading = false,
    onStatusChange,
    onCardClick,
    onRowClick,
    onAddTask,
}) {
    const handleCardClick = onCardClick || onRowClick;
    // ── All hooks must be called unconditionally, before any early returns ────
    const {
        draggedTask,
        dragOverColumn,
        columns,
        handleDragStart,
        handleDragEnd,
        handleDragOver,
        handleDragLeave,
        handleDrop,
    } = useKanbanDrag(tasks, COLUMN_KEYS, onStatusChange);

    const totalTasks = useMemo(() => tasks.length, [tasks]);

    // ── Loading state ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div
                className="
                    overflow-x-auto pb-6
                    [&::-webkit-scrollbar]:h-1.5
                    [&::-webkit-scrollbar-track]:bg-transparent
                    [&::-webkit-scrollbar-thumb]:bg-slate-200
                    [&::-webkit-scrollbar-thumb]:rounded-full
                    dark:[&::-webkit-scrollbar-thumb]:bg-slate-700
                "
            >
                <div className="flex gap-5" style={{ minWidth: KANBAN_COLUMNS.length * (COLUMN_MIN_WIDTH + 20) }}>
                    {KANBAN_COLUMNS.map((col) => (
                        <ColumnSkeleton key={col.key} />
                    ))}
                </div>
            </div>
        );
    }

    // ── Empty board state (no tasks at all, not filtered) ────────────────────

    return (
        <div
            className="
                overflow-x-auto pb-6 select-none
                [&::-webkit-scrollbar]:h-1.5
                [&::-webkit-scrollbar-track]:bg-transparent
                [&::-webkit-scrollbar-thumb]:bg-[#e1e3e4]
                [&::-webkit-scrollbar-thumb]:rounded-full
                dark:[&::-webkit-scrollbar-thumb]:bg-slate-700
            "
            aria-label="Kanban board"
        >
            {totalTasks === 0 ? (
                /* Zero-tasks empty state — renders columns as visual structure */
                <div className="flex gap-5" style={{ minWidth: KANBAN_COLUMNS.length * (COLUMN_MIN_WIDTH + 20) }}>
                    {KANBAN_COLUMNS.map((column) => (
                        <KanbanColumn
                            key={column.key}
                            column={column}
                            tasks={[]}
                            isDropTarget={false}
                            draggedTaskId={null}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onCardClick={handleCardClick}
                            onAddTask={onAddTask}
                        />
                    ))}
                </div>
            ) : (
                <div data-tour="kanban-columns" className="flex gap-5" style={{ minWidth: KANBAN_COLUMNS.length * (COLUMN_MIN_WIDTH + 20) }}>
                    {KANBAN_COLUMNS.map((column) => (
                        <KanbanColumn
                            key={column.key}
                            column={column}
                            tasks={columns.get(column.key) || []}
                            isDropTarget={dragOverColumn === column.key}
                            draggedTaskId={draggedTask?._id || null}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onCardClick={handleCardClick}
                            onAddTask={onAddTask}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
